import { checkIntegrity, Document, NO_ADJUSTMENTS, SCHEMA_VERSION } from "./document";

/**
 * Schema migrations (SPEC.md §9): each step upgrades raw JSON from version n to n+1.
 * Steps operate on untyped data because old documents don't match today's types.
 * Add fixtures for every released version to migrations.test.ts.
 */
type Raw = Record<string, unknown>;
const steps: Record<number, (doc: Raw) => Raw> = {
  // v1 → v2 (phase 2): every node gains `effects`; images gain adjustments, filter, duotone.
  1: (doc) => {
    const nodes = Object.fromEntries(
      Object.entries((doc.nodes ?? {}) as Record<string, Raw>).map(([id, n]) => {
        const next: Raw = { ...n, effects: [] };
        if (n.type === "image") Object.assign(next, { adjust: { ...NO_ADJUSTMENTS }, filter: null, duotone: null });
        return [id, next];
      }),
    );
    return { ...doc, schemaVersion: 2, nodes };
  },
  // v3 → v4: elbow/curved lines, text lists, ruler guides.
  3: (doc) => ({
    ...doc,
    schemaVersion: 4,
    pages: ((doc.pages ?? []) as Raw[]).map((p) => ({ ...p, guides: [] })),
    nodes: Object.fromEntries(
      Object.entries((doc.nodes ?? {}) as Record<string, Raw>).map(([id, n]) => [
        id,
        n.type === "line" ? { ...n, height: 0, routing: "straight" } : n.type === "text" ? { ...n, list: "none" } : n,
      ]),
    ),
  }),
  // v4 → v5 (phase 3): warps gain the "mesh" variant; existing documents are unchanged.
  4: (doc) => ({ ...doc, schemaVersion: 5 }),
  // v5 → v6: consolidates the optional fields added without a bump (colour tools, filters, layer
  // styles/blend modes, conic/diamond paints, raster/adjustment nodes, masks/clip, liquify, puppet
  // warp, saved channels, rasterizedFrom…). All are optional, so documents are unchanged. Legacy
  // `originalAssetId` cut-outs stay as they are: turning them into a mask needs the cut-out's pixels,
  // so the editor keeps handling them at run time (apps/web/src/lib/subject.ts).
  5: (doc) => ({ ...doc, schemaVersion: 6 }),
  // v6 → v7: retouch and paint strokes become data on the photo (`ImageNode.retouchTiles` /
  // `paintTiles`, in source-pixel space) instead of grouped "Retouch"/"Paint" raster layers.
  // Moving existing layers' pixels into the photo needs them decoded and reprojected, so that
  // happens in the editor when it opens the document (engine `foldPhotoLayers`); here the old
  // layers stay valid as they are (`paintOf` / `retouchOf` remain readable).
  6: (doc) => ({ ...doc, schemaVersion: 7 }),
  // v2 → v3: 4-corner warps on every node.
  2: (doc) => ({
    ...doc,
    schemaVersion: 3,
    nodes: Object.fromEntries(Object.entries((doc.nodes ?? {}) as Record<string, Raw>).map(([id, n]) => [id, { ...n, warp: null }])),
  }),
};

export class DocumentError extends Error {
  constructor(
    message: string,
    readonly issues: { path: string; message: string }[] = [],
  ) {
    super(message);
  }
}

/** Upgrade any supported version to the current one and validate it. */
export function loadDocument(input: unknown): Document {
  if (!input || typeof input !== "object") throw new DocumentError("document is not an object");
  let doc = input as Raw;
  if (typeof doc.schemaVersion !== "number" || !Number.isInteger(doc.schemaVersion)) throw new DocumentError("missing schemaVersion");
  let v: number = doc.schemaVersion;
  if (v > SCHEMA_VERSION) throw new DocumentError(`document version ${v} is newer than this app (${SCHEMA_VERSION})`);
  while (v < SCHEMA_VERSION) {
    const step = steps[v];
    if (!step) throw new DocumentError(`no migration from version ${v}`);
    doc = step(doc);
    v = doc.schemaVersion as number;
  }
  return parseDocument(doc);
}

/** Validate a current-version document (schema + integrity). */
export function parseDocument(input: unknown): Document {
  const r = Document.safeParse(input);
  if (!r.success) {
    throw new DocumentError(
      "invalid document",
      r.error.issues.slice(0, 20).map((i) => ({ path: i.path.join("."), message: i.message })),
    );
  }
  const integrity = checkIntegrity(r.data);
  if (integrity.length) throw new DocumentError("document integrity check failed", integrity.slice(0, 20));
  return r.data;
}
